/**
 * StratIQX Icon Management System
 * Centralized icon mapping for service configuration
 */

import React from 'react';
import {
  BarChart3,
  Target,
  RotateCcw,
  Briefcase,
  TrendingUp,
  Users,
  Settings,
  FileText,
  PieChart,
  Activity,
  DollarSign,
  Globe,
  Zap,
  Shield,
  CheckCircle,
  Gift,
  Crown
} from 'lucide-react';

/**
 * Icon component mapping
 * Maps icon names to their respective Lucide React components
 */
const ICON_MAP: Record<string, React.ComponentType<any>> = {
  // Service icons (currently used)
  'BarChart3': BarChart3,
  'Target': Target,
  'RotateCcw': RotateCcw,
  'Gift': Gift,
  'Crown': Crown,

  // Additional business icons (for future services)
  'Briefcase': Briefcase,
  'TrendingUp': TrendingUp,
  'Users': Users,
  'Settings': Settings,
  'FileText': FileText,
  'PieChart': PieChart,
  'Activity': Activity,
  'DollarSign': DollarSign,
  'Globe': Globe,
  'Zap': Zap,
  'Shield': Shield,
  'CheckCircle': CheckCircle
};

/**
 * Get service icon component
 * @param iconName - Name of the icon from ICON_MAP
 * @param className - CSS classes to apply to the icon
 * @returns React component or null if icon not found
 */
export const getServiceIcon = (
  iconName: string, 
  className: string = "h-6 w-6 text-white"
): React.ReactNode => {
  const IconComponent = ICON_MAP[iconName];
  
  if (!IconComponent) {
    console.warn(`Icon '${iconName}' not found in ICON_MAP. Available icons:`, Object.keys(ICON_MAP));
    return null;
  }
  
  return React.createElement(IconComponent, { className });
};

/**
 * Get all available icon names
 * @returns Array of available icon names
 */
export const getAvailableIcons = (): string[] => {
  return Object.keys(ICON_MAP);
};

/**
 * Validate icon name exists
 * @param iconName - Icon name to validate
 * @returns True if icon exists
 */
export const isValidIcon = (iconName: string): boolean => {
  return iconName in ICON_MAP;
};

/**
 * Get icon component class (for TypeScript)
 * @param iconName - Icon name
 * @returns Icon component class or undefined
 */
export const getIconComponent = (iconName: string): React.ComponentType<any> | undefined => {
  return ICON_MAP[iconName];
};

/**
 * Export icon map for direct access if needed
 */
export { ICON_MAP };

/**
 * Default icon to use when service icon is not found
 */
export const DEFAULT_SERVICE_ICON = 'Briefcase';